import { ROUTE_MAP } from "@/common/components/BarChart/constants/routeMap.constant";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { cn } from "@/common/utils/cn";
import { DashboardComponentData } from "@/features/company/types/dashboards/dashboardComponent.type";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

interface XAxisTickProps {
  payload?: { value: any; index: number };
  x?: number;
  y?: number;
  chartData: DashboardComponentData['data'];
  chartDataUpdatedAt: number;
  tiltLabel?: boolean;
}

const ChartXAxisTick = ({
  payload,
  x,
  y,
  chartData,
  chartDataUpdatedAt,
  tiltLabel,
}: XAxisTickProps) => {
  const navigate = useNavigate();

  const index = payload?.index ?? 0;

  const labelValue = chartData.data[index]?.[chartData.primary_settings.query_identifier];

  const dataRow = useMemo(() => {
    return chartData.data[index];
  }, [chartDataUpdatedAt, index]);

  const entityId = dataRow?.meta_attributes?.object_id
  const objectType = dataRow?.meta_attributes?.object_type || ''

  const displayValue = labelValue.length > 25 ? `${labelValue.slice(0, 25)}...` : labelValue

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions();

  const userHasPermissionToSeeEntity = useMemo(() => {
    return isUserPermissionsPending || hasUserPermission(ROUTE_MAP[objectType]?.operationKlass);
  }, [isUserPermissionsPending]);

  const handleClick = useCallback(() => {
    if (!entityId || !objectType || !userHasPermissionToSeeEntity) return;

    const basePath = ROUTE_MAP[objectType]?.url;

    if (basePath) {
      navigate(`${basePath}/${entityId}`);
    }
  }, [entityId, objectType, userHasPermissionToSeeEntity]);

  const isClickable = !!objectType && !!ROUTE_MAP[objectType] && !!entityId && !!userHasPermissionToSeeEntity

  return (
    <g
      transform={
        tiltLabel
          ? `translate(${x}, ${y}) rotate(-25)`
          : undefined
      }
      className={isClickable ? "cursor-pointer group" : ""}
      onClick={isClickable ? handleClick : undefined}
    >
      <text
        x={tiltLabel ? 0 : x}
        y={tiltLabel ? 0 : y}
        dy={35}
        textAnchor={
          tiltLabel
            ? 'middle'
            : 'middle'
        }
        className={cn(
          'text-[10px] font-bold fill-white-300',
          isClickable && 'group-hover:fill-white-800 transition-colors'
        )}
      >
        {displayValue}
      </text>
    </g>
  );
}

export default ChartXAxisTick;
